import 'intersection-observer';
import React from 'react';
import { BreakpointQueryName } from '@doveit/lego';
import { NextPage } from 'next';
import InfoSection, { InfoSectionProps } from '../client/templates/common/info-section/InfoSection';
import HowItWorksSection, { HowItWorksSectionProps } from '../client/templates/common/how-it-works-section/HowItWorksSection';
import BottomBar, { BottomBarProps } from '../client/templates/common/bottom-bar/BottomBar';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import { requireResourcesFromStaticAssets, requireFromStaticAsset } from '../client/utils/assetsUtils';
import scrollToRef from '../client/utils/scroll-to-ref/scrollToRef';
import SectionDivider from '../client/components/section-divider/SectionDivider';
import OurServicesSection, { OurServicesSectionProps } from '../client/templates/common/our-services-section/OurServicesSection';
import { buildEvaluationByLocalityURLObject } from '../client/utils/links';
import PageSection from '../client/components/page-section/PageSection';
import SavingCalculator from '../client/components/saving-calculator/SavingCalculator';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import CityList from '../client/components/city-list/CityList';
import {
  City, HeroSection, TextAlign,
} from '../client/types';
import CreateEvaluation from '../client/containers/create-evaluation/CreateEvaluation';
import { CitiesSectionProps } from '../client/templates/common/cities-section/CitiesSection';
import LogosSection, { LogosSectionProps } from '../client/templates/common/logos-section/LogosSection';
import { getServices } from '../services/our-services/ourServices';
import { sendEvaluationFormSubmitEventAndRedirect } from '../client/utils/tracking';
import useRefInView from '../client/hooks/useRefInView/useRefInView';
import Hero from '../client/templates/common/hero/Hero';
import { getCities } from '../services/cities/cities';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import { importDynamicData } from '../services/importDynamicData';
import LeadInvitationPopup from '../client/components/lead-invitation-popup/LeadInvitationPopup';

export interface SellPropertyProps {
  SEO: PageHeadProps['SEO'],
  hero: HeroSection,
  infoSection: InfoSectionProps,
  citiesSection: CitiesSectionProps,
  cities: City[],
  howItWorksSection: HowItWorksSectionProps,
  channelsSection: LogosSectionProps,
  savingCalculatorSection: {
    title: string,
  },
  bottomBar: BottomBarProps,
  ourServicesSection: OurServicesSectionProps,
}

const INTERSECTION_OPTIONS = { threshold: 0 };

const SellProperty: NextPage<SellPropertyProps> = ({
  SEO,
  hero,
  infoSection,
  citiesSection,
  cities,
  howItWorksSection,
  channelsSection,
  ourServicesSection,
  savingCalculatorSection,
  bottomBar,
}) => {
  const { current, ref: formRef, isVisible: isFormVisible } = useRefInView(INTERSECTION_OPTIONS);

  return (
    <AppLayout activePage="sell-property">
      <PageHead SEO={SEO} />
      <LeadInvitationPopup
        trigger={{
          element: <Hero
            title={hero.title}
            description={hero.description}
            backgroundImage={requireResourcesFromStaticAssets(hero.backgroundImage)}
            secondarySlotTitle={hero.formTitle}
            secondarySlot={(
              <div ref={formRef}>
                <CreateEvaluation
                  onSubmit={() => sendEvaluationFormSubmitEventAndRedirect('hero:create-evaluation')}
                />
              </div>
          )}
          />,
          when: 'out-of-view',
        }}
      />

      <OurServicesSection {...ourServicesSection} />

      <SectionDivider />

      <InfoSection
        title={infoSection.title}
        reasons={infoSection.reasons}
      />

      <HowItWorksSection
        title={howItWorksSection.title}
        picture={howItWorksSection.picture}
        steps={howItWorksSection.steps}
      />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {savingCalculatorSection.title}
          </PageSection.Title>
          <PageSection.Block>
            <SavingCalculator />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <PageSection>
        <PageSection.Wrapper>
          <PageSection.Title textAlign={TextAlign.CENTER}>
            {citiesSection.title}
          </PageSection.Title>
          <PageSection.Description
            text={citiesSection.description}
            textAlign={TextAlign.CENTER}
          />
          <PageSection.Block>
            <CityList
              cities={cities.map((item) => ({
                name: item.title,
                picture: requireFromStaticAsset(item.smallPicture),
                title: `Valutazione immobile a ${item.title}`,
                urlObject: buildEvaluationByLocalityURLObject({ locality: item.locality }),
              }))}
            />
          </PageSection.Block>
        </PageSection.Wrapper>
      </PageSection>

      <SectionDivider />

      <LogosSection
        title={channelsSection.title}
        description={channelsSection.description}
        items={channelsSection.items}
      />

      <BottomBar
        expanded={!isFormVisible}
        hiddenFrom={BreakpointQueryName.MD}
        label={bottomBar.label}
        title={bottomBar.title}
        headline={bottomBar.headline}
        onClick={scrollToRef(current)}
      />
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<SellPropertyProps>(async () => {
  const [common, data, cities, services] = await Promise.all([
    importDynamicData('common.yml'),
    importDynamicData('pages/sell-property.yml'),
    getCities(),
    getServices(),
  ]);

  return {
    props: {
      ...common,
      ...data,
      cities,
      ourServicesSection: {
        ...common.ourServicesSection,
        services,
      },
    },
  };
});

export default withError(SellProperty);
